// [US 6.1] Client d'authentification Sentinel.
// Sécurité §4.6 : access token JWT (15 min) gardé EN MÉMOIRE uniquement (jamais en localStorage).
// Web : refresh token en cookie httpOnly géré par le serveur.
// Android : refresh token retourné dans le body, stocké chiffré (Keystore) via secureTokenStore.
import { Capacitor } from '@capacitor/core';
import { getApiUrl } from './apiUtils';

export interface AuthUser {
    id: number;
    email: string;
    locale: string;
    emailVerified: boolean;
    totpEnabled: boolean;
    premiumUntil: string | null;
    founderSince: string | null; // [US 6.15] membre fondateur (Premium gracieux perpétuel)
    createdAt: string;
}

export interface AuthSession {
    id: number;
    deviceLabel: string | null;
    createdAt: string;
    lastUsedAt: string | null;
}

const isNative = Capacitor.isNativePlatform();

// Access token en mémoire uniquement (exigence §4.6 — jamais en localStorage)
let accessToken: string | null = null;

// Requête de refresh en cours (single-flight)
let refreshInFlight: Promise<boolean> | null = null;

// ---- Identifiant par installation (alerte « nouvel appareil », sentinel_024) ----
// UUID aléatoire généré une fois, sans lien avec le matériel. Web : localStorage ; Android :
// Preferences. Échec de lecture/écriture → undefined : le serveur retombe sur le libellé.
const DEVICE_ID_KEY = 'ssiDeviceId';
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
let deviceIdCache: string | undefined;

async function getDeviceId(): Promise<string | undefined> {
    if (deviceIdCache) return deviceIdCache;
    try {
        let id: string | null;
        if (isNative) {
            const { Preferences } = await import('@capacitor/preferences');
            id = (await Preferences.get({ key: DEVICE_ID_KEY })).value;
        } else {
            id = localStorage.getItem(DEVICE_ID_KEY);
        }
        // Valeur absente ou corrompue : on en régénère une (le schéma serveur exige un UUID)
        if (!id || !UUID_RE.test(id)) {
            id = crypto.randomUUID();
            if (isNative) {
                const { Preferences } = await import('@capacitor/preferences');
                await Preferences.set({ key: DEVICE_ID_KEY, value: id });
            } else {
                localStorage.setItem(DEVICE_ID_KEY, id);
            }
        }
        deviceIdCache = id;
        return id;
    } catch {
        return undefined;
    }
}

// ---- Stockage sécurisé du refresh token (Android uniquement) ----
// Refresh token natif chiffré au repos via Android Keystore (AES-GCM) par
// @aparajita/capacitor-secure-storage. Le web n'écrit JAMAIS le refresh token
// côté JS (cookie httpOnly géré par le serveur) — d'où les gardes `isNative`.
const NATIVE_REFRESH_KEY = 'ssi_refresh_token';

const secureTokenStore = {
    // Lecture fail-safe : une erreur Keystore (corruption, erreur OS) est traitée
    // comme une absence de session — l'utilisateur se reconnecte, jamais de crash.
    async get(): Promise<string | null> {
        if (!isNative) return null;
        try {
            const { SecureStorage } = await import('@aparajita/capacitor-secure-storage');
            return await SecureStorage.getItem(NATIVE_REFRESH_KEY);
        } catch {
            return null;
        }
    },
    // Écriture sans catch volontaire : un échec de persistance du token sécurisé
    // ne doit pas être masqué (remonte à l'appelant — login / refresh).
    async set(token: string): Promise<void> {
        if (!isNative) return;
        const { SecureStorage } = await import('@aparajita/capacitor-secure-storage');
        await SecureStorage.setItem(NATIVE_REFRESH_KEY, token);
    },
    // Suppression best-effort : ne doit jamais bloquer le logout.
    async clear(): Promise<void> {
        if (!isNative) return;
        try {
            const { SecureStorage } = await import('@aparajita/capacitor-secure-storage');
            await SecureStorage.remove(NATIVE_REFRESH_KEY);
        } catch {
            /* best-effort */
        }
    },
};

const jsonHeaders = { 'Content-Type': 'application/json' };

const post = async (path: string, body: object = {}) =>
    fetch(getApiUrl(path), {
        method: 'POST',
        headers: jsonHeaders,
        credentials: 'include',
        body: JSON.stringify(body),
    });

export type LoginResult =
    | { status: 'ok'; user: AuthUser }
    | { status: 'requires2fa' }
    | { status: 'error'; error: string };

export const authClient = {
    getAccessToken: () => accessToken,
    isAuthenticated: () => accessToken !== null,

    async register(email: string, password: string, locale: string): Promise<{ ok: boolean; error?: string }> {
        const res = await post('/api/auth/register', { email, password, locale });
        if (res.ok) return { ok: true };
        const data = await res.json().catch(() => ({}));
        return { ok: false, error: data.error || 'unknown' };
    },

    async login(email: string, password: string, totpCode?: string, recoveryCode?: string): Promise<LoginResult> {
        const res = await post('/api/auth/login', {
            email,
            password,
            totpCode: totpCode || undefined,
            recoveryCode: recoveryCode || undefined,
            platform: isNative ? 'android' : 'web',
            deviceId: await getDeviceId(),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) return { status: 'error', error: data.error || 'unknown' };
        if (data.requires2fa) return { status: 'requires2fa' };
        accessToken = data.accessToken;
        if (isNative && data.refreshToken) await secureTokenStore.set(data.refreshToken);
        return { status: 'ok', user: data.user };
    },

    // Tente de restaurer la session au chargement (cookie web / token natif).
    // Single-flight : les appels concurrents (double effet StrictMode, retry 401 simultanés)
    // partagent la même requête — sinon le 2e rejouerait un refresh déjà consommé.
    tryRefresh(): Promise<boolean> {
        if (!refreshInFlight) {
            refreshInFlight = (async () => {
                const body: { refreshToken?: string; deviceId?: string } = {};
                if (isNative) {
                    const stored = await secureTokenStore.get();
                    if (!stored) return false;
                    body.refreshToken = stored;
                }
                try {
                    body.deviceId = await getDeviceId();
                    const res = await post('/api/auth/refresh', body);
                    if (!res.ok) {
                        if (isNative) await secureTokenStore.clear();
                        return false;
                    }
                    const data = await res.json();
                    accessToken = data.accessToken;
                    if (isNative && data.refreshToken) await secureTokenStore.set(data.refreshToken);
                    return true;
                } catch {
                    return false;
                } finally {
                    refreshInFlight = null;
                }
            })();
        }
        return refreshInFlight;
    },

    async logout(): Promise<void> {
        const body: { refreshToken?: string } = {};
        if (isNative) {
            const stored = await secureTokenStore.get();
            if (stored) body.refreshToken = stored;
        }
        await post('/api/auth/logout', body).catch(() => undefined);
        accessToken = null;
        await secureTokenStore.clear();
    },

    // fetch authentifié : Bearer + retry transparent après refresh si 401
    async fetch(path: string, init: RequestInit = {}): Promise<Response> {
        const doFetch = () => fetch(getApiUrl(path), {
            ...init,
            credentials: 'include',
            headers: {
                ...jsonHeaders,
                ...(init.headers || {}),
                ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
            },
        });
        let res = await doFetch();
        if (res.status === 401 && await this.tryRefresh()) {
            res = await doFetch();
        }
        return res;
    },

    async getMe(): Promise<AuthUser | null> {
        const res = await this.fetch('/api/me');
        if (!res.ok) return null;
        return (await res.json()).user as AuthUser;
    },

    async updateLocale(locale: string): Promise<void> {
        await this.fetch('/api/me', { method: 'PATCH', body: JSON.stringify({ locale }) });
    },

    async verifyEmail(token: string): Promise<boolean> {
        const res = await post('/api/auth/verify-email', { token });
        return res.ok;
    },

    async resendVerification(email: string): Promise<void> {
        await post('/api/auth/resend-verification', { email });
    },

    async forgotPassword(email: string): Promise<void> {
        await post('/api/auth/forgot-password', { email });
    },

    // ---- [US 6.15] Comptes fondateurs (fenêtre d'activation contrôlée serveur) ----

    async foundersWindow(): Promise<{ open: boolean; deadline: string | null }> {
        try {
            const res = await fetch(getApiUrl('/api/founders/window'));
            if (!res.ok) return { open: false, deadline: null };
            return await res.json();
        } catch {
            return { open: false, deadline: null };
        }
    },

    // Chemin non connecté : le serveur envoie un e-mail de confirmation (anti-usurpation).
    async founderActivate(email: string, locale: string): Promise<{ ok: boolean; error?: string }> {
        const res = await post('/api/founders/activate', { email, locale });
        if (res.ok) return { ok: true };
        const data = await res.json().catch(() => ({}));
        return { ok: false, error: data.error || 'unknown' };
    },

    // Chemin connecté : activation immédiate sur le compte authentifié.
    async founderActivateMe(): Promise<{ ok: boolean; error?: string }> {
        const res = await this.fetch('/api/founders/activate-me', { method: 'POST' });
        if (res.ok) return { ok: true };
        const data = await res.json().catch(() => ({}));
        return { ok: false, error: data.error || 'unknown' };
    },

    // Clic sur le lien de l'e-mail (page /founder-confirm).
    async founderConfirm(token: string): Promise<boolean> {
        const res = await post('/api/founders/confirm', { token });
        return res.ok;
    },

    async resetPassword(token: string, password: string): Promise<{ ok: boolean; error?: string }> {
        const res = await post('/api/auth/reset-password', { token, password });
        if (res.ok) return { ok: true };
        const data = await res.json().catch(() => ({}));
        return { ok: false, error: data.error || 'unknown' };
    },

    async listSessions(): Promise<AuthSession[]> {
        const res = await this.fetch('/api/auth/sessions');
        if (!res.ok) return [];
        return (await res.json()).sessions as AuthSession[];
    },

    async revokeSession(id: number): Promise<void> {
        await this.fetch(`/api/auth/sessions/${id}`, { method: 'DELETE' });
    },

    async revokeAllSessions(): Promise<void> {
        await this.fetch('/api/auth/sessions/revoke-all', { method: 'POST', body: '{}' });
        accessToken = null;
        await secureTokenStore.clear();
    },

    async setup2fa(): Promise<{ otpauthUrl: string; qrDataUrl: string; secret: string } | null> {
        const res = await this.fetch('/api/auth/2fa/setup', { method: 'POST', body: '{}' });
        return res.ok ? await res.json() : null;
    },

    async enable2fa(code: string): Promise<{ ok: boolean; recoveryCodes?: string[] }> {
        const res = await this.fetch('/api/auth/2fa/enable', { method: 'POST', body: JSON.stringify({ code }) });
        if (!res.ok) return { ok: false };
        const data = await res.json();
        return { ok: true, recoveryCodes: data.recoveryCodes };
    },

    async disable2fa(code: string): Promise<boolean> {
        const res = await this.fetch('/api/auth/2fa/disable', { method: 'POST', body: JSON.stringify({ code }) });
        return res.ok;
    },

    // Nouveaux codes de récupération (remplacent les anciens) — preuve par code TOTP.
    async regenerateRecoveryCodes(code: string): Promise<{ ok: boolean; recoveryCodes?: string[] }> {
        const res = await this.fetch('/api/auth/2fa/recovery-codes', { method: 'POST', body: JSON.stringify({ code }) });
        if (!res.ok) return { ok: false };
        const data = await res.json();
        return { ok: true, recoveryCodes: data.recoveryCodes };
    },

    async exportMyData(): Promise<Blob | null> {
        const res = await this.fetch('/api/me/export');
        return res.ok ? await res.blob() : null;
    },

    async deleteAccount(password: string): Promise<{ ok: boolean; error?: string }> {
        const res = await this.fetch('/api/me', { method: 'DELETE', body: JSON.stringify({ password }) });
        if (res.ok) {
            accessToken = null;
            await secureTokenStore.clear();
            return { ok: true };
        }
        const data = await res.json().catch(() => ({}));
        return { ok: false, error: data.error || 'unknown' };
    },
};
